import React, { useState } from 'react';
import type { PageDetailOut } from '../../types/catalog';
import type { FillBlanksInteraction, FillBlankSegment } from '../../types/page';
import { isFillBlanksInteraction } from '../../types/page';
import { Button } from '../../../../components/ui/Button';
import { Modal } from '../../../../components/ui/Modal';

interface FillBlanksV1Props {
  page: PageDetailOut;
  onComplete?: () => void;
}

export const FillBlanksV1: React.FC<FillBlanksV1Props> = ({ page, onComplete }) => {
  const interactions = page.page_json_v1?.interactions ?? [];
  const fb = interactions.find(isFillBlanksInteraction) as FillBlanksInteraction | undefined;

  const [fills, setFills] = useState<Record<string, string>>({});
  const [modalBlankId, setModalBlankId] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const [completed, setCompleted] = useState(false);

  if (!fb) {
    return (
      <div className="p-6 text-gray-500 dark:text-gray-400">
        No fill blanks interaction found.
      </div>
    );
  }

  const segments = fb.segments ?? [];
  const correct: Record<string, string> = {};
  segments.forEach((s) => {
    if (s.blank && s.blank_id && s.answer) correct[s.blank_id] = s.answer;
  });

  const allCorrect = Object.keys(correct).every((id) => fills[id] === correct[id]);
  const allFilled = Object.keys(correct).every((id) => !!fills[id]);

  const handleSubmit = () => {
    setSubmitted(true);
    if (allCorrect) {
      setCompleted(true);
      onComplete?.();
    }
  };

  const currentBlank = segments.find((s) => s.blank && s.blank_id === modalBlankId);
  const choices = currentBlank?.choices ?? [];

  return (
    <div className="space-y-6 p-6 overflow-auto">
      <h1 className="text-xl font-semibold text-gray-900 dark:text-white">
        {page.page_title || 'Fill in the blanks'}
      </h1>
      <p className="text-gray-700 dark:text-gray-300">{fb.prompt}</p>
      <div className="flex flex-wrap items-center gap-2 text-xl">
        {segments.map((seg, i) => {
          if (seg.blank && seg.blank_id) {
            const val = fills[seg.blank_id];
            const isWrong = submitted && val && val !== correct[seg.blank_id];
            const isRight = submitted && val === correct[seg.blank_id];
            return (
              <React.Fragment key={i}>
                <button
                  type="button"
                  onClick={() => setModalBlankId(seg.blank_id ?? null)}
                  disabled={submitted}
                  className={`inline-flex items-center justify-center min-w-[3rem] px-3 py-2 rounded-lg border-2 ${
                    isWrong ? 'border-red-500 bg-red-50 dark:bg-red-900/30' :
                    isRight ? 'border-green-500 bg-green-50 dark:bg-green-900/30' :
                    'border-dashed border-gray-400 dark:border-gray-500 hover:border-primary-500'
                  } text-gray-900 dark:text-white font-semibold`}
                >
                  {val || '___'}
                </button>
              </React.Fragment>
            );
          }
          return (
            <span key={i} className="text-gray-900 dark:text-white">
              {seg.text ?? ''}
            </span>
          );
        })}
      </div>
      <div className="flex gap-3">
        <Button
          onClick={handleSubmit}
          disabled={!allFilled || submitted}
        >
          Submit
        </Button>
        {submitted && !allCorrect && (
          <span className="text-red-600 dark:text-red-400 font-medium">Some blanks are wrong.</span>
        )}
        {submitted && allCorrect && (
          <span className="text-green-600 dark:text-green-400 font-medium">Correct!</span>
        )}
      </div>

      <Modal
        isOpen={Boolean(modalBlankId)}
        onClose={() => setModalBlankId(null)}
        title="Choose"
        size="sm"
      >
        <div className="flex flex-wrap gap-2 p-4">
          {choices.map((c) => (
            <button
              key={c}
              type="button"
              onClick={() => {
                if (modalBlankId) setFills((prev) => ({ ...prev, [modalBlankId]: c }));
                setModalBlankId(null);
              }}
              className="px-4 py-2 rounded-lg border-2 border-gray-200 dark:border-gray-700 hover:border-primary-500 text-lg font-semibold"
            >
              {c}
            </button>
          ))}
        </div>
      </Modal>
    </div>
  );
};
